import * as React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import Paper from '@mui/material/Paper'
import styled from '@emotion/styled'

import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import ConstructionIcon from '@mui/icons-material/Construction'
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck'
import EditNoteIcon from '@mui/icons-material/EditNote'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import { useUserContext } from '../contexts/UserContext'

import {
  AttendanceRoutes,
  MaintenanceRoutes,
  FuelRoutes,
  CashflowRoutes,
  RootRoutes,
} from '../utils/routeGenerator'

const StyledBottomNavigation = styled(BottomNavigation)`
  &.MuiBottomNavigation-root {
    height: 70px;
    align-items: baseline;
    padding-top: 10px;
  }
`

// Define all nav items with module + access keys
const NAV_ITEMS = [
  {
    label: 'Reading',
    icon: <NetworkCheckIcon />,
    pathMatch: 'reading',
    moduleKey: 'readings',
    accessKey: 'readings',
    getRoute: (user: any) =>
      RootRoutes.home({ employeeId: user?.userId, page: 1 }),
  },
  {
    label: 'Fuel',
    icon: <LocalGasStationIcon />,
    pathMatch: 'fuel',
    moduleKey: 'fuel',
    accessKey: 'fuel',
    getRoute: () => FuelRoutes.list(),
  },
  {
    label: 'Cashflow',
    icon: <AccountBalanceWalletIcon />,
    pathMatch: 'cashflow',
    moduleKey: 'cashflow',
    accessKey: 'cashflow',
    getRoute: () => CashflowRoutes.home(),
  },
  {
    label: 'Maintenance',
    icon: <ConstructionIcon />,
    pathMatch: 'maintenances',
    moduleKey: 'maintenance',
    accessKey: 'maintenances',
    getRoute: () => MaintenanceRoutes.list(),
  },
  {
    label: 'Attendance',
    icon: <EditNoteIcon />,
    pathMatch: 'attendances',
    moduleKey: 'attendance',
    accessKey: 'attendances',
    getRoute: () => AttendanceRoutes.list(),
  },
]

export default function FixedBottomNavigation() {
  const [value, setValue] = React.useState<number>(0)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, company } = useUserContext()

  const modules = (company as any)?.modules || {}
  const accessList = (user as any)?.accessList || {}

  // Filter: company must have module enabled + user must have access (or access not set = allowed)
  const visibleItems = NAV_ITEMS.filter((item) => {
    const companyEnabled = modules[item.moduleKey] !== false
    const userHasAccess = accessList[item.accessKey] !== false
    return companyEnabled && userHasAccess
  })

  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const idx = visibleItems.findIndex((item) =>
      location.pathname.includes(item.pathMatch)
    )
    if (idx >= 0) setValue(idx)
  }, [location, visibleItems])

  return (
    <Box sx={{ pb: 7 }} ref={ref}>
      <CssBaseline />
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        elevation={3}
      >
        <StyledBottomNavigation
          showLabels
          value={value}
          onChange={(event, newValue) => {
            setValue(newValue)
            const item = visibleItems[newValue]
            if (item) navigate(item.getRoute(user))
          }}
        >
          {visibleItems.map((item) => (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              icon={item.icon}
            />
          ))}
        </StyledBottomNavigation>
      </Paper>
    </Box>
  )
}
